import {useEffect, useState} from "react";
import {Button} from "../../../basic/button";
import {Badge} from "../../../basic/badge";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {FileText, Gauge, Pencil, Save, Trash2, X} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useGetAdminEntitlementsQuery,
    useGetAdminProductsQuery,
    useUpdateLicenseByIdMutation,
    type VwpEntitlement,
    type VwpLicenseItem,
    type VwpUpdateLicenseItem,
    VwpLicenseRead
} from "../../../../store/generated/api.ts";
import {LicenseDetails} from "./LicenseDetails";
import {LicenseForm} from "./LicenseForm";
import {LicenseQuotas} from "./LicenseQuotas";
import {StripeBillingSection} from "./StripeBillingSection";
import {EntitlementSelector} from "../../../shared/selectors/EntitlementSelector";
import {Input} from "../../../basic/input";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";
import {Separator} from "../../../basic/separator";
import {formatDate} from "../../../../utils/dateUtils";

const formatDateForApi = (dateValue: string | undefined): string | undefined => {
    if (!dateValue) return undefined;

    if (dateValue.includes('T')) {
        return dateValue;
    }

    return `${dateValue}T00:00:00Z`;
};

interface EditLicenseDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedLicense: VwpLicenseRead | null;
    isLoading?: boolean;
    error?: any;
}

interface EditingLicenseItem extends VwpLicenseItem {
    isEditing?: boolean;
    isNew?: boolean;
    hasServerId?: boolean;
}

const normalizeLicenseItems = (items: VwpLicenseItem[]): EditingLicenseItem[] => {
    const seenIds = new Set<string>();

    return items.reduce<EditingLicenseItem[]>((acc, item, index) => {
        const stableId = item.id || `item-${index}`;
        if (seenIds.has(stableId)) {
            return acc;
        }

        seenIds.add(stableId);
        acc.push({
            ...item,
            id: stableId,
            hasServerId: !!item.id,
            isEditing: false,
            isNew: false
        });

        return acc;
    }, []);
};

export function EditLicenseDialog({
                                      open,
                                      onOpenChange,
                                      selectedLicense,
                                      isLoading = false,
                                      error
                                  }: EditLicenseDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [editedLicenseData, setEditedLicenseData] = useState<Record<string, any>>({});
    const [selectedEntitlements, setSelectedEntitlements] = useState<VwpEntitlement[]>([]);
    const [licenseItems, setLicenseItems] = useState<EditingLicenseItem[]>([]);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [savingItemId, setSavingItemId] = useState<string | null>(null);

    const [updateLicenseById, {isLoading: isUpdating}] = useUpdateLicenseByIdMutation();

    const {
        data: entitlementsData,
        isLoading: entitlementsLoading,
        error: entitlementsError
    } = useGetAdminEntitlementsQuery();

    const {
        data: productsData,
        isLoading: productsLoading
    } = useGetAdminProductsQuery({page: 0, size: 100});

    const availableEntitlements = entitlementsData?.availableEntitlements || [];
    const availableProducts = productsData?.products || [];

    const getProductNameById = (productId: string): string => {
        const product = availableProducts.find(p => p.id === productId);
        return product?.name || productId || '-';
    };

    useEffect(() => {
        if (selectedLicense) {
            setEditedLicenseData({
                quantity: selectedLicense.quantity,
                status: selectedLicense.status,
                trialStatus: selectedLicense.trialStatus,
                firstInvoiceId: selectedLicense.firstInvoiceId,
                subscriptionId: selectedLicense.subscriptionId
            });
            setSelectedEntitlements(selectedLicense.entitlements || []);
            setLicenseItems(normalizeLicenseItems(selectedLicense.items || []));
        } else {
            setEditedLicenseData({});
            setSelectedEntitlements([]);
            setLicenseItems([]);
        }
    }, [selectedLicense]);

    const handleEntitlementsChange = (entitlements: VwpEntitlement[]) => {
        setSelectedEntitlements(entitlements);
    };

    const handleEditItem = (itemId: string) => {
        setEditingItemId(itemId);
        setLicenseItems(items => items.map(item =>
            item.id === itemId ? {...item, isEditing: true} : item
        ));
    };

    const handleCancelEditItem = (itemId: string) => {
        setEditingItemId(null);
        setLicenseItems(items => {
            const item = items.find(i => i.id === itemId);
            if (item?.isNew) {
                return items.filter(i => i.id !== itemId);
            }
            return items.map(item =>
                item.id === itemId ? {...item, isEditing: false} : item
            );
        });
    };

    const buildItemsForApi = (items: EditingLicenseItem[]): VwpUpdateLicenseItem[] => {
        return items.map(item => {
            const payload: VwpUpdateLicenseItem = {
                productId: item.productId,
                status: item.status,
                startDate: formatDateForApi(item.startDate),
                expiryDate: formatDateForApi(item.expiryDate),
                lastInvoiceItemId: item.lastInvoiceItemId,
                referencePurchaseId: item.referencePurchaseId,
                invoiceItemIdHistory: item.invoiceItemIdHistory
            };
            // Only forward the real server id for existing items so the license
            // server updates them in place instead of creating duplicates. New
            // items (and items without a server id) are created without an id.
            if (!item.isNew && item.hasServerId && item.id) {
                payload.id = item.id;
            }
            return payload;
        });
    };

    const handleSaveItem = async (itemId: string) => {
        if (!selectedLicense) return;

        const itemToSave = licenseItems.find(item => item.id === itemId);
        if (!itemToSave?.productId) {
            showErrorToast({message: t('admin.licenses.editLicense.errors.updateFailed')});
            return;
        }

        setSavingItemId(itemId);
        try {
            const updatedLicense = await updateLicenseById({
                licenseId: selectedLicense.id,
                vwpUpdateLicenseRequest: {
                    quantity: selectedLicense.quantity,
                    status: selectedLicense.status,
                    trialStatus: selectedLicense.trialStatus,
                    firstInvoiceId: selectedLicense.firstInvoiceId,
                    subscriptionId: selectedLicense.subscriptionId,
                    entitlements: selectedLicense.entitlements || [],
                    items: buildItemsForApi(licenseItems)
                }
            }).unwrap();

            setEditingItemId(null);
            setLicenseItems(normalizeLicenseItems(updatedLicense.items || []));
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.licenses.editLicense.errors.updateFailed')});
        } finally {
            setSavingItemId(null);
        }
    };

    const handleItemFieldChange = (itemId: string, field: keyof VwpLicenseItem, value: any) => {
        setLicenseItems(items => items.map(item =>
            item.id === itemId ? {...item, [field]: value} : item
        ));
    };

    const handleDeleteItem = (itemId: string) => {
        setLicenseItems(items => items.filter(item => item.id !== itemId));
    };

    const handleAddItem = () => {
        const newItem: EditingLicenseItem = {
            id: `new-${Date.now()}`,
            productId: '',
            name: '',
            status: 'ACTIVE',
            isEditing: true,
            isNew: true
        };
        setLicenseItems(items => [...items, newItem]);
        setEditingItemId(newItem.id);
    };

    const handleSave = async () => {
        if (!selectedLicense) return;

        if (!editedLicenseData.quantity || editedLicenseData.quantity < 1) {
            showErrorToast({message: t('admin.licenses.editLicense.errors.quantityMin')});
            return;
        }

        if (editedLicenseData.quantity < selectedLicense.assigned) {
            showErrorToast({message: t('admin.licenses.editLicense.errors.cannotReduceSeats', {assigned: selectedLicense.assigned})});
            return;
        }

        const items = buildItemsForApi(licenseItems);

        try {
            await updateLicenseById({
                licenseId: selectedLicense.id,
                vwpUpdateLicenseRequest: {
                    quantity: editedLicenseData.quantity,
                    status: editedLicenseData.status || selectedLicense.status,
                    trialStatus: editedLicenseData.trialStatus,
                    firstInvoiceId: editedLicenseData.firstInvoiceId,
                    subscriptionId: editedLicenseData.subscriptionId,
                    entitlements: selectedEntitlements,
                    items: items.length > 0 ? items : []
                }
            }).unwrap();

            toast.success(t('admin.licenses.editLicense.successWithEntitlements', {product: selectedLicense.product}));

            onOpenChange(false);
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.licenses.editLicense.errors.updateFailed')});
        }
    };

    const handleCancel = () => {
        onOpenChange(false);
        setEditedLicenseData({});
        setSelectedEntitlements([]);
        setLicenseItems([]);
        setEditingItemId(null);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('admin.licenses.editLicense.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.licenses.editLicense.description')}
                    </DialogDescription>
                </DialogHeader>

                {isLoading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-destructive">
                            {error?.data?.message || t('admin.licenses.error.loadingFailed')}
                        </div>
                    </div>
                )}

                {!isLoading && !error && selectedLicense && (
                    <div className="flex-1 overflow-y-auto space-y-6 py-4">
                        <LicenseDetails selectedLicense={selectedLicense}/>

                        <CollapsibleSection
                            title={t('admin.licenses.editLicense.generalInformation')}
                            icon={<FileText className="h-4 w-4"/>}
                            defaultOpen={true}
                        >
                            <LicenseForm
                                selectedLicense={selectedLicense}
                                editedLicenseData={editedLicenseData}
                                setEditedLicenseData={setEditedLicenseData}
                            />
                        </CollapsibleSection>

                        <CollapsibleSection
                            title={t('admin.licenses.stripeBilling')}
                            icon={<FileText className="h-4 w-4"/>}
                        >
                            <StripeBillingSection
                                selectedLicense={selectedLicense}
                                editedLicenseData={editedLicenseData}
                                setEditedLicenseData={setEditedLicenseData}
                                onLicenseUpdated={(updatedLicense) => {
                                    setEditedLicenseData(prev => ({
                                        ...prev,
                                        subscriptionId: updatedLicense.subscriptionId,
                                        firstInvoiceId: updatedLicense.firstInvoiceId
                                    }));
                                }}
                            />
                        </CollapsibleSection>

                        <Separator className="my-4"/>

                        <CollapsibleSection
                            title={t('admin.licenses.editLicense.quotas.title')}
                            icon={<Gauge className="h-4 w-4"/>}
                        >
                            <LicenseQuotas
                                licenseId={selectedLicense.id}
                                quotas={selectedLicense.quotas || []}
                            />
                        </CollapsibleSection>

                        <Separator className="my-4"/>

                        <CollapsibleSection title={t('admin.licenses.editLicense.showAdvanced')}>
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold">
                                        {t('admin.licenses.editLicense.licenseItems')}
                                    </h4>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddItem}
                                    >
                                        {t('admin.licenses.editLicense.addItem')}
                                    </Button>
                                </div>
                                    {licenseItems.length > 0 ? (
                                        <div className="border rounded-md">
                                            <table className="w-full">
                                                <thead>
                                                <tr className="border-b bg-muted/50">
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.product')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.startDate')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.expiryDate')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.status')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.lastInvoiceItemId')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.referencePurchaseId')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium">
                                                        {t('admin.licenses.editLicense.invoiceItemIdHistory')}
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-medium w-32">
                                                        {t('common.actions')}
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {licenseItems.map((item) => (
                                                    <tr key={item.id} className="border-b">
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <select
                                                                    value={item.productId}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'productId', e.target.value)}
                                                                    className="h-8 px-2 border rounded text-sm w-full"
                                                                >
                                                                    <option value="">Select a product</option>
                                                                    {availableProducts.map(product => (
                                                                        <option key={product.id} value={product.id}>
                                                                            {product.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <span
                                                                    className="text-sm">{getProductNameById(item.productId)}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <Input
                                                                    type="date"
                                                                    value={item.startDate ? item.startDate.split('T')[0] : ''}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'startDate', e.target.value)}
                                                                    className="h-8"
                                                                />
                                                            ) : (
                                                                <span className="text-sm">
                                                                        {formatDate(item.startDate)}
                                                                    </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <Input
                                                                    type="date"
                                                                    value={item.expiryDate ? item.expiryDate.split('T')[0] : ''}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'expiryDate', e.target.value)}
                                                                    className="h-8"
                                                                />
                                                            ) : (
                                                                <span className="text-sm">
                                                                        {formatDate(item.expiryDate)}
                                                                    </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <select
                                                                    value={item.status}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'status', e.target.value)}
                                                                    className="h-8 px-2 border rounded text-sm"
                                                                >
                                                                    <option value="ACTIVE">ACTIVE</option>
                                                                    <option value="DISABLED">DISABLED</option>
                                                                </select>
                                                            ) : (
                                                                <Badge
                                                                    variant={item.status === 'ACTIVE' ? 'default' : 'outline'}
                                                                >
                                                                    {item.status}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <Input
                                                                    type="text"
                                                                    value={item.lastInvoiceItemId || ''}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'lastInvoiceItemId', e.target.value || undefined)}
                                                                    className="h-8"
                                                                    placeholder="in_..."
                                                                />
                                                            ) : (
                                                                <span className="text-sm text-muted-foreground">
                                                                    {item.lastInvoiceItemId || '-'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <Input
                                                                    type="text"
                                                                    value={item.referencePurchaseId || ''}
                                                                    onChange={(e) => handleItemFieldChange(item.id, 'referencePurchaseId', e.target.value || undefined)}
                                                                    className="h-8"
                                                                    placeholder="pi_..."
                                                                />
                                                            ) : (
                                                                <span className="text-sm text-muted-foreground">
                                                                    {item.referencePurchaseId || '-'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {item.isEditing ? (
                                                                <Input
                                                                    type="text"
                                                                    value={item.invoiceItemIdHistory?.join(', ') || ''}
                                                                    onChange={(e) => {
                                                                        const history = e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : undefined;
                                                                        handleItemFieldChange(item.id, 'invoiceItemIdHistory', history);
                                                                    }}
                                                                    className="h-8"
                                                                    placeholder="in_..., in_..."
                                                                />
                                                            ) : (
                                                                <span
                                                                    className="text-sm text-muted-foreground max-w-[200px] truncate block">
                                                                    {item.invoiceItemIdHistory?.join(', ') || '-'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div className="flex gap-1">
                                                                {item.isEditing ? (
                                                                    <>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0"
                                                                            onClick={() => handleSaveItem(item.id)}
                                                                            disabled={savingItemId === item.id || isUpdating}
                                                                        >
                                                                            <Save className="h-4 w-4"/>
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0"
                                                                            onClick={() => handleCancelEditItem(item.id)}
                                                                            disabled={savingItemId === item.id || isUpdating}
                                                                        >
                                                                            <X className="h-4 w-4"/>
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0"
                                                                            onClick={() => handleEditItem(item.id)}
                                                                        >
                                                                            <Pencil className="h-4 w-4"/>
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-destructive"
                                                                            onClick={() => handleDeleteItem(item.id)}
                                                                        >
                                                                            <Trash2 className="h-4 w-4"/>
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            {t('admin.licenses.editLicense.noItems')}
                                        </div>
                                    )}
                                </div>
                        </CollapsibleSection>

                        <Separator className="my-4"/>

                        <CollapsibleSection title={t('admin.licenses.editLicense.entitlements.title')}>
                                {entitlementsLoading && (
                                    <div className="flex items-center justify-center py-8">
                                        <p>{t('common.loading')}</p>
                                    </div>
                                )}

                                {entitlementsError && (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="text-center">
                                            <p className="text-red-500">{t('common.error')}</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {t('admin.licenses.editLicense.entitlements.loadError')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {!entitlementsLoading && !entitlementsError && (
                                    <EntitlementSelector
                                        availableEntitlements={availableEntitlements}
                                        selectedEntitlements={selectedEntitlements}
                                        onEntitlementsChange={handleEntitlementsChange}
                                        loading={entitlementsLoading}
                                        error={entitlementsError ? t('admin.licenses.editLicense.entitlements.loadError') : undefined}
                                    />
                                )}
                        </CollapsibleSection>
                    </div>
                )}

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={handleCancel}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={isUpdating}>
                        {isUpdating ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
